// Publish flow at `/publish`. Structure from Figma Make's
// SubmitReportPage — 5-step wizard collapsed into a single long page with
// four Cards (Фирма / Отчет / Документи / Контакт+Фактура).
//
// TODO: form is stateful-only; wire to a POST endpoint + validation.
// Follow-up could adopt react-hook-form + zod + shadcn <Form>.

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  H1,
  Input,
  Label,
  P,
  Separator,
} from '@podium/platform/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { AlertWord } from '../components/brand'

export const Route = createFileRoute('/publish')({
  component: PublishPage,
})

type FormState = {
  company_name: string
  eik_bulstat: string
  city: string
  address: string
  vat_registered: boolean
  report_year: string
  representative_name: string
  prepared_by: string
  certified_by: string
  balance_pdf: File | null
  income_statement_pdf: File | null
  activity_report_pdf: File | null
  accounting_policy_pdf: File | null
  no_activity_pdf: File | null
  email: string
  phone: string
  fax: string
}

const EMPTY: FormState = {
  company_name: '',
  eik_bulstat: '',
  city: '',
  address: '',
  vat_registered: false,
  report_year: String(new Date().getFullYear() - 1),
  representative_name: '',
  prepared_by: '',
  certified_by: '',
  balance_pdf: null,
  income_statement_pdf: null,
  activity_report_pdf: null,
  accounting_policy_pdf: null,
  no_activity_pdf: null,
  email: '',
  phone: '',
  fax: '',
}

function PublishPage() {
  const [form, setForm] = useState<FormState>(EMPTY)

  const setField =
    <K extends keyof FormState>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [key]: e.target.value } as FormState)

  const setFile = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({
      ...form,
      [key]: e.target.files?.[0] ?? null,
    } as FormState)

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // TODO: POST to /api/reports once the submit endpoint exists.
    console.log('publish submission', form)
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <div className="mb-6 space-y-2">
        <H1>Публикуване на годишен финансов отчет</H1>
        <P className="text-muted-foreground">
          Попълнете данните на предприятието и прикачете сканираните страници на отчета (PDF). След
          плащане отчетът ще бъде публикуван в рамките на 24 часа. Цената за самообслужване е{' '}
          <AlertWord>7 лв. / 3.58 €</AlertWord> (с ДДС). <a href="/prices">Вижте цени и условия</a>.
        </P>
      </div>

      <form className="space-y-6" onSubmit={onSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Данни на фирма / организация</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="company_name">Наименование</Label>
              <Input
                id="company_name"
                required
                value={form.company_name}
                onChange={setField('company_name')}
                placeholder="ООД Примерна Фирма"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="eik_bulstat">ЕИК / БУЛСТАТ</Label>
                <Input
                  id="eik_bulstat"
                  required
                  value={form.eik_bulstat}
                  onChange={setField('eik_bulstat')}
                  inputMode="numeric"
                  pattern="[0-9]{9}|[0-9]{13}"
                  placeholder="9 или 13 цифри"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Град</Label>
                <Input
                  id="city"
                  required
                  value={form.city}
                  onChange={setField('city')}
                  placeholder="София"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Адрес</Label>
              <Input
                id="address"
                required
                value={form.address}
                onChange={setField('address')}
                placeholder="ул. Примерна 123"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="vat_registered"
                checked={form.vat_registered}
                onCheckedChange={(v) => setForm({ ...form, vat_registered: v === true })}
              />
              <Label htmlFor="vat_registered" className="cursor-pointer">
                Регистрирана по ЗДДС
              </Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Данни за отчета</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="report_year">Отчетна година</Label>
                <Input
                  id="report_year"
                  type="number"
                  min={2000}
                  max={2100}
                  required
                  value={form.report_year}
                  onChange={setField('report_year')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="representative_name">Представляващ / МОЛ</Label>
                <Input
                  id="representative_name"
                  required
                  value={form.representative_name}
                  onChange={setField('representative_name')}
                  placeholder="Иван Иванов"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="prepared_by">Изготвил</Label>
                <Input
                  id="prepared_by"
                  value={form.prepared_by}
                  onChange={setField('prepared_by')}
                  placeholder="Име на счетоводител"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="certified_by">Заверил (одитор)</Label>
                <Input
                  id="certified_by"
                  value={form.certified_by}
                  onChange={setField('certified_by')}
                  placeholder="Име на регистриран одитор"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Документи за публикуване</CardTitle>
            <CardDescription>
              Прикачете PDF файлове, максимум 10 MB всеки. Задължителните документи са отбелязани
              със <AlertWord>*</AlertWord>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FileField
              label="Баланс"
              required
              file={form.balance_pdf}
              onChange={setFile('balance_pdf')}
            />
            <FileField
              label="Отчет за приходи и разходи"
              required
              file={form.income_statement_pdf}
              onChange={setFile('income_statement_pdf')}
            />
            <FileField
              label="Доклад за дейността"
              file={form.activity_report_pdf}
              onChange={setFile('activity_report_pdf')}
            />
            <FileField
              label="Счетоводна политика"
              file={form.accounting_policy_pdf}
              onChange={setFile('accounting_policy_pdf')}
            />
            <FileField
              label="Декларация за липса на дейност"
              file={form.no_activity_pdf}
              onChange={setFile('no_activity_pdf')}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Данни за контакт и фактура</CardTitle>
            <CardDescription>
              На посочения E-mail ще получите фактурата и линка към публикацията.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={setField('email')}
                placeholder="your@email.com"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="phone">Телефон</Label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={setField('phone')}
                  placeholder="+359 888 123 456"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fax">Факс</Label>
                <Input
                  id="fax"
                  value={form.fax}
                  onChange={setField('fax')}
                  placeholder="Опционално"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Separator />

        <div className="flex flex-wrap items-center gap-4">
          <Button type="submit" size="lg">
            Подай за публикуване
          </Button>
          <P className="text-sm text-muted-foreground">
            С натискане на бутона потвърждавате <a href="/terms">Общите условия</a>.
          </P>
        </div>
      </form>
    </div>
  )
}

type FileFieldProps = {
  label: string
  required?: boolean
  file: File | null
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

function FileField({ label, required, file, onChange }: FileFieldProps) {
  const id = `file_${label.replace(/\s+/g, '_')}`
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label}
        {required && (
          <>
            {' '}
            <AlertWord>*</AlertWord>
          </>
        )}
      </Label>
      <Input id={id} type="file" accept="application/pdf" required={required} onChange={onChange} />
      {file && (
        <P className="text-xs text-muted-foreground">
          {file.name} · {(file.size / 1024).toFixed(0)} KB
        </P>
      )}
    </div>
  )
}
